import { currencySymbol } from '../../shared/money';
import type { SelectedModel } from './create.types';

const REVENUE_SPLIT_COLORS = ['#15110f', '#8f6f4d', '#587166', '#b87a55', '#6f6b78', '#a68a62'];
const UNALLOCATED_SPLIT_COLOR = '#ddd6ce';

export interface RevenueSplitChartSegment {
  key: string;
  label: string;
  value: number;
  color: string;
}

function clampPercent(value: number | string | null | undefined): number {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.max(0, Math.min(100, Math.round(numericValue ?? 0)));
}

function getModelSplitLabel(model: SelectedModel, index: number): string {
  if (model.source === 'organizer_self') return 'You as model';
  return model.displayName || model.email || `Model ${index + 1}`;
}

export function formatSplitPercent(value: number): string {
  return Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`;
}

export function moneyInputPaddingLeft(currency: string): string {
  const prefixLength = Math.max(currencySymbol(currency).length, 1);
  return `calc(${prefixLength}ch + 1.5rem)`;
}

export function buildRevenueSplitChartState(options: {
  selectedModels: SelectedModel[];
  showPaidModelTerms: boolean;
  isEvenSplit: boolean;
  organizerRemainder: number;
  selectedOrganizerModel: SelectedModel | null;
}) {
  const { selectedModels, showPaidModelTerms, isEvenSplit, organizerRemainder, selectedOrganizerModel } = options;
  const baseSegments: RevenueSplitChartSegment[] = showPaidModelTerms && selectedModels.length > 0
    ? (() => {
      const recipients = [
        ...selectedModels.map((model, index) => ({
          key: model.id ?? `${model.source ?? 'model'}:${model.profileId ?? model.email ?? model.displayName}:${index}`,
          label: getModelSplitLabel(model, index),
          value: isEvenSplit
            ? 0
            : clampPercent(model.source === 'organizer_self' ? organizerRemainder : model.splitPercent),
        })),
        ...(!selectedOrganizerModel ? [{
          key: 'organizer-remainder',
          label: 'You',
          value: isEvenSplit ? 0 : organizerRemainder,
        }] : []),
      ];

      if (isEvenSplit) {
        const evenShare = 100 / recipients.length;
        return recipients.map((recipient, index) => ({
          ...recipient,
          value: evenShare,
          color: REVENUE_SPLIT_COLORS[index % REVENUE_SPLIT_COLORS.length],
        }));
      }

      return recipients
        .filter((recipient) => recipient.value > 0)
        .map((recipient, index) => ({
          ...recipient,
          color: REVENUE_SPLIT_COLORS[index % REVENUE_SPLIT_COLORS.length],
        }));
    })()
    : [];

  const allocatedTotal = baseSegments.reduce((sum, segment) => sum + segment.value, 0);
  const segments = allocatedTotal > 0 && allocatedTotal < 100
    ? [
      ...baseSegments,
      {
        key: 'unallocated',
        label: 'Unallocated',
        value: 100 - allocatedTotal,
        color: UNALLOCATED_SPLIT_COLOR,
      },
    ]
    : baseSegments;
  const gradientTotal = Math.max(100, segments.reduce((sum, segment) => sum + segment.value, 0));
  const gradient = segments.length > 0
    ? (() => {
      let cursor = 0;
      return segments.map((segment) => {
        const start = cursor;
        const end = cursor + (segment.value / gradientTotal) * 100;
        cursor = end;
        return `${segment.color} ${start}% ${end}%`;
      }).join(', ');
    })()
    : UNALLOCATED_SPLIT_COLOR;
  const accessibleLabel = segments.length > 0
    ? `Revenue split chart: ${segments.map((segment) => `${segment.label} ${formatSplitPercent(segment.value)}`).join(', ')}`
    : 'Revenue split chart';

  return {
    segments,
    gradient,
    accessibleLabel,
  };
}
